import {ref, App as VueApp, computed} from 'vue'
import emitter from '@/services/infrastructure/emitter'
import {LOGOUT, NO_ORG_SELECTED, ORG_SELECTED, ORG_SWITCHED} from '@/types/events'
import {get} from '@/services/infrastructure/api.service'
import {useVatServiceKey} from '@/types/InjectionKeys'
import _ from 'lodash'
import {toIsoDate} from '@/dateUtils/dateUtils'
import {TransactionEntryLine} from '@/types/transaction/TransactionEntryLine'
import {CUSTOMER_ACCOUNT_NO, SUPPLIER_ACCOUNT_NO} from '@/bookkeeping-account/accountTypes'
import {VatOption} from '@/vat/types/VatOptions'

const VAT_PATH = '/vat/options'
const options = ref<Array<VatOption>>([])

const fetchVatOptions = async () => {
    try {
        const fetchedOptions = await get<VatOption[]>(VAT_PATH, {}) ?? []
        options.value = fetchedOptions.toSorted((a: VatOption, b: VatOption) => a.id - b.id)
    } catch (e: any) {
        //     TODO add error message
    }
}

let initialized = false
const init = () => {
    if (initialized) return
    initialized = true
    fetchVatOptions()
        .catch(err => {
            console.error('Error fetching VAT Options', err)
        })
}

const clear = () => {
    options.value = []
}

emitter.on(ORG_SWITCHED, fetchVatOptions)
emitter.on(ORG_SELECTED, init)
emitter.on(NO_ORG_SELECTED, clear)
emitter.on(LOGOUT, clear)



const findDefaultVatOption = (validVatOptions: Array<VatOption>) => {
    const codeZero = validVatOptions.find(opt => opt.code === 0 && !opt.specificationKey)
    if (codeZero) {
        return codeZero
    }
    // Removed throw since we do encounter timing issues here (vatOptions not yet loaded)
    return undefined
}

const findValidVatOptionFromId = (validVatOptions: Array<VatOption>, vatOptionId: number) => {
    return validVatOptions.find(opt => opt.id == vatOptionId) ?? findDefaultVatOption(validVatOptions)
}

export const calculateVatAmount = (vatOption: VatOption, amount: number) => {
    if (vatOption.rate === 1) {
        return amount
    } else {
        if (vatOption.exclusive) {
            return amount * vatOption.rate
        } else {
            return amount - amount / (1 + vatOption.rate)
        }
    }
}

export const isDebitVatEditable = (line: TransactionEntryLine): boolean => {
    return !!line.debitAccount
      && line.debitAccount !== CUSTOMER_ACCOUNT_NO
      && line.debitAccount !== SUPPLIER_ACCOUNT_NO
}

export const isCreditVatEditable = (line: TransactionEntryLine): boolean => {
    return !!line.creditAccount
        && line.creditAccount !== CUSTOMER_ACCOUNT_NO
        && line.creditAccount !== SUPPLIER_ACCOUNT_NO
}

const useVatService = () => {
    const getValidVatOptionsForDate = (date: Date) => {
        const formattedDate = toIsoDate(date)
        return options.value.filter((option) => option.activeFrom <= formattedDate && formattedDate <= option.activeTo)
    }

    const getVatOptionById = (id: number) => {
        return options.value.find((option) => option.id === id)
    }

    const findVatOptionFromCode = (code: number) => {
        return _.find(options.value, {code})
    }

    const incomeVatOptions = computed(() => {
        const codes = [3, 5, 6, 7, 31, 32, 51, 52]
        return options.value
            .filter(option => codes.includes(option.code))
            .filter(option => !option.specificationKey)
            || []
    })

    const getLabelForVatPicker = (vatOption: VatOption, showCompact: boolean = false) => {
        if(vatOption?.specificationKey){
            return 'vat-codes.' + vatOption.specificationKey
        } else {
            return showCompact ? 'MVA' : ''
        }
    }

    return {
        vatOptions: options,
        getVatOptionById,
        getValidVatOptionsForDate,
        findVatOptionFromCode,
        incomeVatOptions,
        getLabelForVatPicker
    }
}

const plugin = {
    install(app: VueApp) {
        app.provide(useVatServiceKey, useVatService)
    }
}

const getTermNumber = (month: number): number => {
    return Math.ceil(month/2)
}

export {
    useVatService,
    findValidVatOptionFromId,
    findDefaultVatOption,
    plugin,
    getTermNumber,
}
